import { OrganizationPrepareCapture, PaymentMethod } from 'api/interfaces'
import { defaultTransformFn, platformApi } from 'api/rtkQueryApi/platform/platformApi'

const paymentMethodsTags = platformApi.enhanceEndpoints({ addTagTypes: ['Cards', 'BankAccounts', 'PaymentMethods', 'Balances'] })

export const paymentMethodsApi = paymentMethodsTags.injectEndpoints({
  endpoints: build => ({
    preparePaymentMethod: build.query<OrganizationPrepareCapture, { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `accounts/${accountId}/capture`,
          method: 'POST',
          data: {},
          isContext: true
        }
      },
      keepUnusedDataFor: 0,
      transformResponse: defaultTransformFn
    }),
    getCards: build.query<PaymentMethod[], { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `accounts/${accountId}/cards`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'Cards', id: accountId }]
    }),
    getBankAccounts: build.query<PaymentMethod[], { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `accounts/${accountId}/bankAccounts`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'BankAccounts', id: accountId }]
    }),
    getPaymentMethods: build.query<PaymentMethod[], { accountId: string }>({
      query: ({ accountId }) => {
        return {
          url: `accounts/${accountId}/paymentMethods`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'PaymentMethods', id: accountId }],
      keepUnusedDataFor: 1
    }),
    deletePaymentMethod: build.mutation<void, { accountId: string; paymentMethodId: string }>({
      query: ({ accountId, paymentMethodId }) => ({
        url: `accounts/${accountId}/paymentMethods/${paymentMethodId}`,
        method: 'DELETE',
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'PaymentMethods', id: accountId },
        { type: 'BankAccounts', id: accountId },
        { type: 'Cards', id: accountId },
        { type: 'Balances', id: accountId }
      ]
    }),
    assignDefaultPaymentMethod: build.mutation<void, { accountId: string; paymentMethodId: string }>({
      query: ({ accountId, paymentMethodId }) => ({
        url: `accounts/${accountId}/paymentMethods/${paymentMethodId}/makeDefault`,
        method: 'POST',
        data: {},
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'PaymentMethods', id: accountId },
        { type: 'BankAccounts', id: accountId },
        { type: 'Cards', id: accountId },
        { type: 'Balances', id: accountId }
      ]
    }),
    updatePaymentMethodNickname: build.mutation<void, { accountId: string; paymentMethodId: string; nickname: string }>({
      query: ({ accountId, paymentMethodId, nickname }) => ({
        url: `accounts/${accountId}/paymentMethods/${paymentMethodId}/nickname`,
        method: 'POST',
        data: { nickname },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'PaymentMethods', id: accountId },
        { type: 'BankAccounts', id: accountId },
        { type: 'Cards', id: accountId },
        { type: 'Balances', id: accountId }
      ]
    })
  })
})

export const { usePreparePaymentMethodQuery, useLazyPreparePaymentMethodQuery, useGetPaymentMethodsQuery, useGetCardsQuery, useGetBankAccountsQuery, useDeletePaymentMethodMutation, useAssignDefaultPaymentMethodMutation, useUpdatePaymentMethodNicknameMutation } = paymentMethodsApi
